import * as React from "react";
import { Table, OverlayTrigger } from "react-bootstrap";
import { snakeCase } from "change-case";
import I18n from "lib/I18n";
import { fromBigNumber } from "lib/Any64bitId";
import { BigNumber } from "bignumber.js";
import style from "./style.module.css";
import tooltips from "../../lib/tooltips";
import {
  BsExclamationCircle,
  BsExclamationTriangle,
  BsInfoCircle,
  BsSearch,
} from "react-icons/bs";
import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { getStore } from "../../store";
import { Item } from "../../store/Item";
import { FilterInterface } from "../../store/Filter";

const TableHeader = observer(() => {
  const { app } = getStore();

  return (
    <thead>
      <tr>
        {!app.idScrambled && <th key="status" />}
        {app.columns.map((c, i) => (
          <th key={i}>{snakeCase(c)}</th>
        ))}
      </tr>
    </thead>
  );
});

function statusIcon(
  icon: React.ReactElement,
  colorClass: string,
  messageKey: string,
  index: number,
) {
  return (
    <OverlayTrigger key={index} placement="top" overlay={tooltips[messageKey]}>
      <span className={`status-icon text-${colorClass}`}>{icon}</span>
    </OverlayTrigger>
  );
}

function pushStatusIcon(
  icon: React.ReactElement,
  colorClass: string,
  messageKey: string,
  icons: JSX.Element[],
) {
  return icons.push(statusIcon(icon, colorClass, messageKey, icons.length));
}

function getStatusIcons(item: Item) {
  const icons: JSX.Element[] = [];
  if (item.hasError()) {
    pushStatusIcon(
      <BsExclamationCircle />,
      "danger",
      "has_data_type_error",
      icons,
    );
  }
  if (item.hasUndefinedKeys()) {
    pushStatusIcon(
      <BsExclamationTriangle />,
      "warning",
      "has_undefined_keys",
      icons,
    );
  }
  if (item.eventMasterNotDefined()) {
    pushStatusIcon(<BsInfoCircle />, "info", "event_master_not_defined", icons);
  }
  return icons;
}

interface TableColumnProps {
  column: string;
  value: string | number | BigNumber;
  onClickId: () => void;
}

const TableColumn = observer((props: TableColumnProps) => {
  const { app, list, config } = getStore();
  const [isFocused, setIsFocused] = useState(false);

  const onMouseEnter = () => {
    setIsFocused(true);
  };

  const onMouseLeave = () => {
    setIsFocused(false);
  };

  const renderCellContent = () => {
    const { column, value, onClickId } = props;

    switch (column) {
      case "id":
        return (
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          <a href={`#${value}`} onClick={onClickId}>
            {" "}
            {value as number}{" "}
          </a>
        );
      case "postedAt":
        return I18n.l("date.formats.short", value as string);
      case "nsaId":
      case "deviceId2":
        if (value) {
          const formedValue = config.displayIdAsHex
            ? fromBigNumber(value)
            : value.toString();
          return (
            <span>
              <span className={style.tableEllipsisCell}>{formedValue}</span>
              &nbsp;
              {!app.idScrambled && renderFilterIcon(column, formedValue)}
            </span>
          );
        }
        return value;
      case "eventId":
        if (value) {
          return (
            <span>
              {value as string}
              &nbsp;
              {renderFilterIcon(column, value)}
            </span>
          );
        }
        return value;
      case "serialNumber":
        if (value) {
          return (
            <span>
              <span className={style.tableEllipsisCell}>{value as string}</span>
              &nbsp;
              {!app.idScrambled && renderFilterIcon(column, value)}
            </span>
          );
        }
        return value;
      default:
        if (BigNumber.isBigNumber(value)) {
          return fromBigNumber(value);
        }
        return value;
    }
  };

  const renderFilterIcon = (
    name: keyof FilterInterface,
    value: string | number | BigNumber,
  ) => {
    if (isFocused) {
      const onClick = () => {
        list.filter[name] = fromBigNumber(value);
        list.search(1);
      };
      return (
        <span onClick={onClick} className="search-by-value">
          <BsSearch />
        </span>
      );
    }
    return (
      <span style={{ opacity: 0 }}>
        <BsSearch />
      </span>
    );
  };

  const { column } = props;
  return (
    <td
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-raw-post-column={column}
    >
      {renderCellContent()}
    </td>
  );
});

interface TableRowProps {
  item: Item;
  isSelected: boolean;
  onScroll: (top: number, height: number) => void;
}

export const TableRow = observer((props: TableRowProps) => {
  const { app, list } = getStore();
  const { item, isSelected, onScroll } = props;
  const tableRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (isSelected && tableRowRef.current) {
      const elem = tableRowRef.current;
      onScroll(elem.offsetTop, elem.clientHeight);
    }
  }, []);

  const cells = app.columns.map((column, index) => (
    <TableColumn
      key={`key${index}`}
      column={column}
      value={item[column]}
      onClickId={() => list.selectPost(item.id)}
    />
  ));
  return (
    <tr
      data-raw-post-id={item.id}
      className={isSelected ? "bg-info" : undefined}
      ref={tableRowRef}
    >
      {!app.idScrambled && (
        <td key="status" data-raw-post-column="status">
          {getStatusIcons(item)}
        </td>
      )}
      {cells}
    </tr>
  );
});

export const PostTable = observer(() => {
  const { list } = getStore();
  const boundingBoxRef = useRef<HTMLDivElement>(null);

  const onScroll = (top: number, height: number) => {
    if (!boundingBoxRef.current) {
      return;
    }
    const boundingBox = boundingBoxRef.current;
    const bottom = top + height;
    const scrollTop = boundingBox.scrollTop;
    const clientHeight = boundingBox.clientHeight;
    const scrollBottom = scrollTop + clientHeight;

    if (top < scrollTop) {
      boundingBox.scrollTop = top;
    } else if (bottom > scrollBottom) {
      boundingBox.scrollTop = top - clientHeight + height;
    }
  };

  return (
    <div className={style.tableBox} ref={boundingBoxRef}>
      <Table striped hover size="sm" className="raw-post-table">
        <TableHeader />
        <tbody>
          {list.items.map((item, index) => (
            <TableRow
              key={index}
              item={item}
              isSelected={item.id === list.selectedId}
              onScroll={onScroll}
            />
          ))}
        </tbody>
      </Table>
    </div>
  );
});
